import { apiClient } from './axios';

import type { DashboardConfig, TreeNodeMetric } from '@repo/shared-types';

export interface DashboardConfigListItem {
  id: string;
  name: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface DashboardConfigDetail {
  id: string;
  name: string;
  config: DashboardConfig;
  is_default: boolean;
  type: string;
  created_at: string;
  updated_at: string;
}

export interface MetricOption {
  key: string;
  description: string;
}

export const dashboardConfigApi = {
  getMetricOptions: async (): Promise<MetricOption[]> => {
    const response = await apiClient.get<MetricOption[]>('/analytics/metric-options');
    return response.data;
  },

  list: async (): Promise<DashboardConfigListItem[]> => {
    const response = await apiClient.get<DashboardConfigListItem[]>('/analytics/dashboard-configs');
    return response.data;
  },

  getById: async (id: string): Promise<DashboardConfigDetail> => {
    const response = await apiClient.get<DashboardConfigDetail>(
      `/analytics/dashboard-configs/${id}`,
    );
    return response.data;
  },

  create: async (data: {
    name: string;
    config: DashboardConfig;
    isDefault?: boolean;
    type?: string;
  }): Promise<{ id: string }> => {
    const response = await apiClient.post<{ id: string }>('/analytics/dashboard-configs', data);
    return response.data;
  },

  update: async (
    id: string,
    data: { name?: string; config?: DashboardConfig; type?: string },
  ): Promise<{ id: string }> => {
    const response = await apiClient.put<{ id: string }>(
      `/analytics/dashboard-configs/${id}`,
      data,
    );
    return response.data;
  },

  setDefault: async (id: string): Promise<void> => {
    await apiClient.patch(`/analytics/dashboard-configs/${id}/set-default`);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/analytics/dashboard-configs/${id}`);
  },

  getData: async (id: string): Promise<Record<string, TreeNodeMetric>> => {
    const response = await apiClient.get<Record<string, TreeNodeMetric>>(
      `/analytics/dashboard-configs/${id}/data`,
      { params: { direction: 'ALL', timePreset: 'all' } },
    );
    return response.data;
  },
};
